<template>
  <section class="py-5 web-design-page">
    <div class="container">
      <!-- 頁面標題與服務介紹 -->
      <header class="text-center mb-5">
        <h1 class="page-title">{{ $t('introduce.sections.websiteIntro') }}</h1>
      </header>

      <div class="row justify-content-center">
        <div class="col-lg-10 col-xl-9">
          <div class="mb-4">
            <h2 class="fw-bold fs-3 text-dark">
              {{ $t('webDesign.title') }} Web Design
              <span class="text-secondary fw-light mx-2">|</span>
              {{ $t('webDesign.subTitle') }}
            </h2>
          </div>

          <p class="text-secondary lh-lg mb-4">
            {{ $t('webDesign.description') }}
          </p>

          <div class="mb-5">
            <h5 class="fw-bold mb-3">{{ $t('webDesign.advantagesTitle') }}</h5>
            <ul class="list-unstyled ps-2">
              <li v-for="(item, index) in advantages" :key="index" class="mb-2 d-flex align-items-start">
                <span class="me-2 text-dark">•</span>
                <span class="text-secondary">{{ $t(item) }}</span>
              </li>
            </ul>
          </div>

          <p class="text-secondary mb-5">
            {{ $t('webDesign.conclusion') }}
          </p>

          <!-- 參考網站標題 -->
          <div class="mb-4">
            <h3 class="fw-bold fs-4 text-dark">{{ $t('webDesign.portfolioTitle') }}</h3>
          </div>

          <!-- 網站介紹卡片 (垂直陳列) -->
          <div v-for="(item, idx) in items" :key="idx" class="mb-5">
            <a :href="item.link" target="_blank" rel="noopener noreferrer" class="card-link-wrapper">
              <div class="card border-light shadow-sm bg-white overflow-hidden card-hover">
                <div class="card-body p-0 bg-light text-center position-relative">
                  <div class="ratio ratio-16x9">
                    <img
                      :src="item.image"
                      :alt="item.title"
                      class="img-fluid w-100 object-fit-cover"
                    />
                  </div>
                  <div class="card-img-overlay d-flex justify-content-center align-items-center" style="background-color: rgba(0, 0, 0, 0.3)">
                    <h4 class="card-title text-white fw-bold" style="font-size: 2.5rem; text-shadow: 2px 2px 4px rgba(0,0,0,0.5);">
                      {{ item.title }}
                    </h4>
                  </div>
                </div>
                <!-- 新增的卡片下方說明 -->
                <div class="card-footer bg-white border-0 pt-3 pb-2 px-3">
                  <p class="mb-0 text-secondary small">
                    <strong class="text-dark">{{ $t('webDesign.portfolioLabel') }}</strong> {{ $t(item.client) }}
                  </p>
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
  <section class="container py-5">
  <div class="row justify-content-center">
    <div class="col-lg-10 col-xl-9">
      
      <div class="mb-4">
        <h2 class="fw-bold text-dark mb-3">
          {{ $t('webDesign.app.title') }}
          <span class="text-secondary fw-light mx-2">|</span> 
          {{ $t('webDesign.app.subTitle') }}
        </h2>
      </div>

      <p class="text-secondary lh-lg mb-4">
        {{ $t('webDesign.app.description') }}
      </p>

      <div class="mb-4">
        <h5 class="fw-bold mb-3 text-dark">{{ $t('webDesign.app.servicesTitle') }}</h5>
        <ul class="list-unstyled ps-2">
          <li v-for="(item, index) in serviceItems" :key="index" class="mb-2 d-flex align-items-start">
            <span class="me-2 text-dark">•</span>
            <span class="text-secondary">{{ $t(item) }}</span>
          </li>
        </ul>
      </div>

      <p class="text-secondary mt-4">
        {{ $t('webDesign.app.conclusion') }}
      </p>

    </div>
  </div>
</section>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

// 定義優勢列表數據
const advantages = computed(() => [
  'webDesign.advantages[0]',
  'webDesign.advantages[1]',
  'webDesign.advantages[2]',
  'webDesign.advantages[3]',
  'webDesign.advantages[4]'
]);

// ===========================================
// VUE 數據新增：手機 APP 服務內容 (新增)
// ===========================================
const serviceItems = computed(() => [
  'webDesign.app.services[0]',
  'webDesign.app.services[1]',
  'webDesign.app.services[2]',
  'webDesign.app.services[3]',
  'webDesign.app.services[4]'
]);

// 網站介紹資料 (使用翻譯 keys)
const items = computed(() => [
  {
    title: 'Yarn Web',
    client: 'webDesign.clients.joudy',
    description: t('introduce.product1.content'),
    image: '/inritek-web/images/introduce1.png',
    link: 'https://inritek.com.tw/yarn-web/#/'
  },
  {
    title: 'Amigrumi Web',
    client: 'webDesign.clients.bobi',
    description: t('introduce.product2.content'),
    image: '/inritek-web/images/introduce2.png',
    link: 'https://inritek.com.tw/amigurumi-web/#/'
  },
  {
    title: 'WONYARN Web',
    client: 'webDesign.clients.wonyarn',
    description: t('introduce.product3.content'),
    image: '/inritek-web/images/introduce3.png',
    link: 'https://wonyarn.com/'
  }
])
</script>

<style scoped>
/* 雖然使用了 Bootstrap，但微調一些字距可以讓質感更接近設計稿 */
.page-title { font-size: 2.5rem; font-weight: 700; margin-bottom: 0.5rem; }

h1, h2, h3, h4, h5 {
  letter-spacing: 0.5px;
}

p, li {
  color: #555;
}

.object-fit-cover { object-fit: cover; }

/* 卡片連結包裝器 */
.card-link-wrapper {
  display: block;
  text-decoration: none;
  color: inherit;
  width: 100%;
}

/* 卡片 hover 效果 */
.card-hover {
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}
.card-link-wrapper:hover .card-hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

.container {
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}
</style>
